import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Award, Users, Send, Mail, Loader2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const AdminRankingTab = () => {
  const { getToken } = useAuth();
  const [rankingData, setRankingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifying, setNotifying] = useState(false);

  const fetchRanking = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API}/admin/ranking`, { headers: { Authorization: `Bearer ${token}` } });
      setRankingData(res.data);
    } catch { toast.error('Erro ao carregar ranking'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchRanking(); }, [fetchRanking]);

  const notifyTopRanking = async () => {
    setNotifying(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/ranking/notify`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.emails_sent > 0) toast.success(`${res.data.emails_sent} e-mail(s) enviado(s)!`);
      else toast.info('Nenhum novo dono no Top 3 para notificar.');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao enviar notificacoes'); }
    finally { setNotifying(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-4 border-[#C5A059] border-t-transparent rounded-full" /></div>;
  if (!rankingData) return <div className="py-12 text-center text-slate-400 text-sm">Erro ao carregar dados do ranking.</div>;

  return (
    <div className="space-y-6" data-testid="ranking-section">
      <Card className="border-0 shadow-sm bg-gradient-to-r from-[#C5A059]/5 to-[#C5A059]/10 border-l-4 border-l-[#C5A059]">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div><p className="font-semibold text-slate-900 text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-[#C5A059]" />Notificar Top 3 por E-mail</p><p className="text-xs text-slate-500 mt-0.5">Envia e-mail de parabens para donos que entraram no podio (max 1x por dia por dono)</p></div>
          <Button onClick={notifyTopRanking} disabled={notifying} className="bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-semibold text-xs gap-2" data-testid="notify-ranking-btn">{notifying ? <><Loader2 className="w-4 h-4 animate-spin" />Enviando...</> : <><Send className="w-4 h-4" />Enviar Notificacao</>}</Button>
        </CardContent>
      </Card>

      {/* Owner Rankings */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-[#0A2647] to-[#0A2647]/90 text-white">
          <CardTitle className="text-base flex items-center gap-2"><Award className="w-5 h-5 text-[#C5A059]" />Top Donos - Mais Engajados</CardTitle>
          <CardDescription className="text-slate-300">Classificacao por total de avaliacoes, promotores e convites Google</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {rankingData.owner_rankings.length > 0 ? (
            <>
              {rankingData.owner_rankings.length >= 3 && (
                <div className="bg-gradient-to-b from-[#0A2647]/5 to-transparent px-6 pt-8 pb-4">
                  <div className="flex items-end justify-center gap-4 max-w-lg mx-auto">
                    {[1, 0, 2].map(idx => {
                      const o = rankingData.owner_rankings[idx];
                      const isFirst = idx === 0;
                      return (
                        <div key={idx} className="flex flex-col items-center flex-1">
                          <div className={`${isFirst ? 'w-14 h-14 bg-[#C5A059] ring-[#C5A059]/50 shadow-lg' : idx === 1 ? 'w-12 h-12 bg-slate-200 ring-slate-300' : 'w-12 h-12 bg-amber-100 ring-amber-200'} rounded-full flex items-center justify-center mb-2 ring-2`}>
                            <span className={`${isFirst ? 'text-xl text-white' : idx === 1 ? 'text-lg text-slate-600' : 'text-lg text-amber-700'} font-bold`}>{idx + 1}</span>
                          </div>
                          <p className="text-xs font-bold text-slate-700 text-center truncate w-full">{o.company_name}</p>
                          <p className={`text-[10px] ${isFirst ? 'text-[#C5A059] font-semibold' : 'text-slate-400'}`}>{o.total_feedbacks} aval.</p>
                          <div className={`w-full ${isFirst ? 'h-24 bg-[#C5A059]/20 border-t-4 border-[#C5A059]' : idx === 1 ? 'h-16 bg-slate-200' : 'h-10 bg-amber-100'} rounded-t-lg mt-2`} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="ranking-owners-table">
                  <thead><tr className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500"><th className="text-center px-3 py-3 w-12">#</th><th className="text-left px-4 py-3">Empresa</th><th className="text-center px-3 py-3">Plano</th><th className="text-center px-3 py-3">Avaliacoes</th><th className="text-center px-3 py-3">Mes Atual</th><th className="text-center px-3 py-3">Promotores</th><th className="text-center px-3 py-3">Convites Google</th><th className="text-center px-3 py-3">Indicacoes</th><th className="text-center px-3 py-3">Score</th></tr></thead>
                  <tbody>
                    {rankingData.owner_rankings.map((o, idx) => (
                      <tr key={o.company_id} className={`border-b border-slate-100 hover:bg-slate-50 ${idx < 3 ? 'bg-[#C5A059]/[0.03]' : ''}`} data-testid={`ranking-owner-${idx}`}>
                        <td className="text-center px-3 py-3">{idx === 0 ? '\u{1F947}' : idx === 1 ? '\u{1F948}' : idx === 2 ? '\u{1F949}' : <span className="text-xs font-bold text-slate-400">{idx + 1}</span>}</td>
                        <td className="px-4 py-3"><p className="font-semibold text-slate-900">{o.company_name}</p><p className="text-xs text-slate-400">{o.owner_name}</p></td>
                        <td className="text-center px-3 py-3"><span className="text-xs font-semibold px-2 py-1 rounded-full bg-[#0A2647]/10 text-[#0A2647]">{o.plan_name}</span></td>
                        <td className="text-center px-3 py-3 font-bold text-slate-700">{o.total_feedbacks}</td>
                        <td className="text-center px-3 py-3 text-blue-600 font-medium">{o.monthly_feedbacks}</td>
                        <td className="text-center px-3 py-3"><span className="font-bold text-[#C5A059]">{o.promoters}</span></td>
                        <td className="text-center px-3 py-3 text-emerald-600 font-medium">{o.google_invites}</td>
                        <td className="text-center px-3 py-3 text-slate-500">{o.referral_count}</td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-[#0A2647] bg-[#C5A059]/10 px-2.5 py-1 rounded-full">{o.score}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : <div className="py-12 text-center text-slate-400 text-sm">Nenhuma empresa cadastrada ainda.</div>}
        </CardContent>
      </Card>

      {/* Affiliate Rankings */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-emerald-700 to-emerald-800 text-white">
          <CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5 text-emerald-200" />Top Afiliados - Mais Conversoes</CardTitle>
          <CardDescription className="text-emerald-200/80">Classificacao por conversoes e comissoes acumuladas</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {rankingData.affiliate_rankings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ranking-affiliates-table">
                <thead><tr className="bg-slate-50 border-b text-xs text-slate-500"><th className="text-center px-3 py-3 w-12">#</th><th className="text-left px-4 py-3">Afiliado</th><th className="text-center px-3 py-3">Status</th><th className="text-center px-3 py-3">Conversoes</th><th className="text-center px-3 py-3">Total Ganho</th><th className="text-center px-3 py-3">Pendente</th><th className="text-center px-3 py-3">Score</th></tr></thead>
                <tbody>
                  {rankingData.affiliate_rankings.map((a, idx) => (
                    <tr key={a.affiliate_id} className={`border-b border-slate-100 hover:bg-slate-50 ${idx < 3 ? 'bg-emerald-50/50' : ''}`} data-testid={`ranking-aff-${idx}`}>
                      <td className="text-center px-3 py-3">{idx === 0 ? '\u{1F947}' : idx === 1 ? '\u{1F948}' : idx === 2 ? '\u{1F949}' : <span className="text-xs font-bold text-slate-400">{idx + 1}</span>}</td>
                      <td className="px-4 py-3"><p className="font-semibold text-slate-900">{a.name}</p><p className="text-xs text-slate-400">{a.email}</p></td>
                      <td className="text-center px-3 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{a.status === 'approved' ? 'Ativo' : 'Inativo'}</span></td>
                      <td className="text-center px-3 py-3 font-bold text-slate-700">{a.total_conversions}</td>
                      <td className="text-center px-3 py-3 text-emerald-600 font-bold">R$ {a.total_earned.toFixed(2)}</td>
                      <td className="text-center px-3 py-3 text-amber-600 font-medium">R$ {a.total_pending.toFixed(2)}</td>
                      <td className="text-center px-3 py-3"><span className="text-sm font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">{Math.round(a.score)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div className="py-12 text-center text-slate-400 text-sm">Nenhum afiliado com conversoes ainda.</div>}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRankingTab;
